import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FormDialog } from '../form/FormDialog';
import { TextField } from '../form/fields';
import { ConfirmDialog } from './ConfirmDialog';

export interface RecordUsage {
  /** «4 позиции в заказах» */
  title: string;
  /** «заказы № 4471, № 4468 и ещё два» */
  details?: string;
}

export interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Название записи — его нужно ввести для подтверждения. */
  recordTitle: string;
  /** Где используется запись (из ответа API). Непустой список — удаление невозможно. */
  usage?: readonly RecordUsage[];
  onConfirm: () => Promise<unknown>;
  /** Предложить архивирование вместо удаления (если запись используется). */
  onArchiveInstead?: () => void;
  /**
   * Пояснение окна «Удаление невозможно» для записей, у которых архивирования нет (справочники):
   * по умолчанию текст предлагает архивирование.
   */
  blockedLead?: string;
  /** Пояснение окна удаления; по умолчанию — текст прототипа для черновика. */
  lead?: string;
}

/**
 * «Удалить» (README «Опасные действия»): нужно ввести название записи; если запись используется,
 * окно показывает, где именно, и предлагает архивирование.
 */
export function DeleteConfirmDialog({
  open,
  onOpenChange,
  recordTitle,
  usage = [],
  onConfirm,
  onArchiveInstead,
  blockedLead,
  lead,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation(['dialogs', 'form']);
  const blocked = usage.length > 0;
  const schema = useMemo(
    () =>
      z.object({
        confirm: z
          .string()
          .trim()
          .min(1, t('form:required'))
          .refine((value) => value === recordTitle.trim(), t('delete.mismatch')),
      }),
    [recordTitle, t],
  );
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { confirm: '' },
  });

  useEffect(() => {
    if (open) form.reset({ confirm: '' });
  }, [open, form]);

  if (blocked) {
    return (
      <ConfirmDialog
        open={open}
        onOpenChange={onOpenChange}
        width={620}
        overline={t('delete.overline')}
        title={t('delete.blockedTitle')}
        lead={blockedLead ?? t('delete.blockedLead')}
        footNote={blockedLead ? undefined : t('delete.blockedFootNote')}
        confirmLabel={onArchiveInstead ? t('archive.submit') : undefined}
        onConfirm={
          onArchiveInstead
            ? async () => {
                onArchiveInstead();
              }
            : undefined
        }
      >
        <ul className="mb-18 overflow-hidden rounded-18 border border-line-strong">
          {usage.map((item) => (
            <li
              key={item.title}
              className="flex items-start gap-10 border-b border-line-soft px-14 py-12 last:border-b-0"
            >
              <span
                aria-hidden="true"
                className="mt-7 size-6 shrink-0 rounded-pill bg-status-error"
              />
              <span className="flex min-w-0 flex-col gap-2">
                <span className="text-[13px] font-medium text-ink-900">{item.title}</span>
                {item.details ? (
                  <span className="text-[11px] leading-[16px] text-ink-500">{item.details}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </ConfirmDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      onSubmit={() => onConfirm()}
      width={620}
      overline={t('delete.overline')}
      title={t('delete.title', { title: recordTitle })}
      lead={lead ?? t('delete.lead')}
      note={t('delete.note')}
      footNote={t('delete.footNote')}
      submitLabel={t('delete.submit')}
      submitVariant="danger"
      invalidMessage={t('delete.mismatchAlert')}
    >
      <div className="grid grid-cols-2 gap-x-14 gap-y-12">
        <TextField<z.infer<typeof schema>>
          name="confirm"
          label={t('delete.confirmLabel')}
          placeholder={recordTitle}
          required
          wide
          autoComplete="off"
        />
      </div>
    </FormDialog>
  );
}
